<script setup lang="ts">
import {
  ref,
  computed,
  watch,
  nextTick,
  onMounted,
  onUnmounted,
  type ComponentPublicInstance,
} from "vue";
import { useTranslation } from "i18next-vue";
import { Facade, DraggerType } from "@/drawing";
import DraggerMenuState from "@/stores/DraggerMenuState";
import DrawerMenuContent from "../DrawerMenuContent.vue";
import AnalysisSummaryHeader from "./AnalysisSummaryHeader.vue";
import AnalysisGroupCard from "./AnalysisGroupCard.vue";
import AnalysisLaunchDialog from "@/components/canvas/analysis/AnalysisLaunchDialog.vue";
import ObservationsDrawerTabs from "../observations/ObservationsDrawerTabs.vue";
import DrawingLinkDialog from "../observations/DrawingLinkDialog.vue";
import ConfirmationModal from "@/components/generic/ConfirmationModal.vue";
import DRBtn from "@/components/UI/DRBtn.vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import { usePlanAnalysisState } from "@/stores/PlanAnalysisState";
import { useDeviceState } from "@/stores/DeviceState";
import type { ObservationsDrawerTab } from "@/stores/ObservationsPanelState";
import { usePlanAnalysis } from "@/composable/usePlanAnalysis";
import { useAnalysisLaunch } from "@/composable/useAnalysisLaunch";
import { useDrawingDiagnosticLink } from "@/composable/useDrawingDiagnosticLink";
import type {
  AnalysisEntity,
  AnalysisGroup,
  AnalysisGroupKind,
} from "@/utils/plan-analysis/types";
import type { PlanAnalysisGroupEdit } from "@/types";

/**
 * Onglet « IA » du volet Observations : parcours de validation des groupes
 * détectés sur le plan par l'analyse. Un groupe validé devient une
 * observation — elle se retrouve dans l'onglet Observations et se classe par
 * partie dans l'onglet Parties. Monté seulement quand une analyse existe
 * (cf. `ObservationsDrawer`).
 */

const props = defineProps<{
  drawingId: number;
  /** Onglets proposés par le volet (un seul = pas de barre d'onglets). */
  tabs: ObservationsDrawerTab[];
}>();

defineEmits<{
  (e: "close"): void;
}>();

const { t } = useTranslation();
const state = usePlanAnalysisState();
const device = useDeviceState();

// Valider exige un plan rattaché à un ouvrage — mêmes briques que l'onglet
// Observations.
const { isLoadingDrawing, diagnosticId } = useDrawingDiagnosticLink(
  props.drawingId,
);
const showLinkDialog = ref(false);

// ── Effacement complet de l'analyse (repartir de zéro) ──────────────────────
const showClearConfirm = ref(false);
const isClearing = ref(false);

const handleClearAnalysis = async (): Promise<void> => {
  isClearing.value = true;
  await state.clearAnalysis();
  showClearConfirm.value = false;
  isClearing.value = false;
};

const {
  runAnalysis,
  validateGroup,
  ignoreGroup,
  restoreGroup,
  locateGroup,
  getGroupEntities,
  focusEntity,
  removeEntityFromGroup,
  moveEntityToGroup,
  createGroupFromSelection,
  correctGroup,
  exportFixture,
} = usePlanAnalysis(diagnosticId);

// Relance depuis l'onglet IA : même modale de validation que le premier
// lancement (unité du plan), avec l'avertissement de remplacement.
const { showLaunchDialog, suggestedUnit, requestAnalysis, confirmLaunch } =
  useAnalysisLaunch(runAnalysis);

// ── Entités du groupe courant (liste dépliable de sa carte) ─────────────────
// Relues du plan à chaque changement de groupe courant ou de découpage.
const currentGroupEntities = computed<AnalysisEntity[]>(() => {
  const group = state.currentGroup;
  return group ? getGroupEntities(group) : [];
});

const handleEntityRemove = (group: AnalysisGroup, handle: string): void => {
  removeEntityFromGroup(group, handle);
};

/** Une photo « à confirmer » déplacée vers l'un de ses groupes alternatifs. */
const handleEntityMove = (
  group: AnalysisGroup,
  payload: { handle: string; targetGroupId: string },
): void => {
  moveEntityToGroup(group, payload.handle, payload.targetGroupId);
};

// ── Sélection du plan (réactive) et ajout d'éléments au groupe ──────────────

/** Handles sélectionnés sur le plan, suivis via SelectionService.onChange. */
const selectionHandles = ref<string[]>([]);
let unsubscribeSelection: (() => void) | null = null;

onMounted(() => {
  if (Facade.isInitialized) {
    selectionHandles.value = Facade.selection.handles;
    unsubscribeSelection = Facade.selection.onChange((handles) => {
      selectionHandles.value = handles;
    });
  }
});
onUnmounted(() => {
  unsubscribeSelection?.();
});

/**
 * Création d'un groupe à la main depuis la sélection (footer statique) :
 * actif dès qu'une entité est sélectionnée ; le nouveau groupe devient
 * courant, en mode édition, prêt à être complété.
 */
const handleCreateGroup = (): void => {
  addModeActive.value = false;
  createGroupFromSelection();
};

/** Nb d'entités sélectionnées HORS du groupe courant (bouton « Ajouter les X »). */
const extraSelectedCount = computed(() => {
  const group = state.currentGroup;
  if (!group) return 0;
  const inGroup = new Set(group.handles);
  return selectionHandles.value.filter((h) => !inGroup.has(h)).length;
});

/** Ajoute la sélection courante du plan au groupe (entités oubliées). */
const handleAddSelected = (group: AnalysisGroup): void => {
  correctGroup(group, "add");
};

/**
 * Mode « cliquez sur les éléments à rajouter » : l'outil sélection est
 * activé, et chaque entité touchée rejoint le groupe courant à la volée.
 */
const addModeActive = ref(false);

const toggleAddMode = (): void => {
  addModeActive.value = !addModeActive.value;
  if (addModeActive.value) {
    DraggerMenuState.setActiveDragger({ name: DraggerType.Select });
  }
};

// Chaque tap en mode ajout rejoint le groupe, puis la sélection est vidée
// pour enchaîner (le garde-fou évite la boucle sur le clear).
let addingFromSelection = false;
watch(selectionHandles, (handles) => {
  const group = state.currentGroup;
  if (
    !addModeActive.value ||
    addingFromSelection ||
    handles.length === 0 ||
    !group ||
    state.statusOf(group.id) !== "pending"
  ) {
    return;
  }
  const inGroup = new Set(group.handles);
  if (!handles.some((h) => !inGroup.has(h))) return;
  addingFromSelection = true;
  try {
    correctGroup(group, "add");
    Facade.selection.clear();
  } finally {
    addingFromSelection = false;
  }
});

// Changer de groupe (ou le valider/ignorer) sort du mode ajout.
watch(
  [(): string | null => state.currentGroupId, (): unknown => state.statuses],
  () => {
    addModeActive.value = false;
  },
);

/** Persiste une retouche nom / commentaire / type du groupe. */
const handleEditChange = (
  group: AnalysisGroup,
  patch: PlanAnalysisGroupEdit,
): void => {
  state.setGroupEdit(group.id, patch);
};

// ── Validation ──────────────────────────────────────────────────────────────
const validatingGroupId = ref<string | null>(null);

const handleValidate = async (
  group: AnalysisGroup,
  payload: {
    name?: string;
    description?: string;
    kind: AnalysisGroupKind;
  },
): Promise<void> => {
  // Plan sans ouvrage : on propose d'abord la liaison, la validation se
  // relance ensuite à la main.
  if (!diagnosticId.value) {
    showLinkDialog.value = true;
    return;
  }
  validatingGroupId.value = group.id;
  try {
    // L'observation est créée sans partie : elle se classe ensuite dans
    // l'onglet Parties.
    await validateGroup(group, payload);
  } finally {
    validatingGroupId.value = null;
  }
};

// ── Groupe courant : zoom + défilement de la liste ──────────────────────────
const cardEls = new Map<string, HTMLElement>();

const setCardRef = (
  groupId: string,
  el: Element | ComponentPublicInstance | null,
): void => {
  const dom =
    el && "$el" in (el as ComponentPublicInstance)
      ? ((el as ComponentPublicInstance).$el as HTMLElement)
      : (el as HTMLElement | null);
  if (dom) cardEls.set(groupId, dom);
  else cardEls.delete(groupId);
};

watch(
  () => state.currentGroupId,
  async (groupId) => {
    if (!groupId) return;
    await nextTick();
    cardEls.get(groupId)?.scrollIntoView({
      block: "nearest",
      behavior: "smooth",
    });
  },
);

</script>

<template>
  <DrawerMenuContent
    :label="t('canvas:observations.title')"
    :description="t('canvas:analysis.description')"
    actions
  >
    <template v-if="tabs.length > 1" #header>
      <ObservationsDrawerTabs :tabs="tabs" :diagnostic-id="diagnosticId" />
    </template>

    <!-- Chargement du lien plan↔ouvrage -->
    <div v-if="isLoadingDrawing" class="text-center py-5">
      <span class="spinner-border spinner-border-sm" />
    </div>

    <template v-else>
      <!-- Progression de validation, en tête de liste -->
      <AnalysisSummaryHeader
        class="mb-2"
        @export-fixture="exportFixture"
        @clear="showClearConfirm = true"
      />

      <!-- Plan non lié : la validation est bloquée, pas le parcours -->
      <div
        v-if="!diagnosticId"
        class="not-linked d-flex align-items-center gap-2 mb-2"
      >
        <PhosphorIcon name="link-break" :size="16" />
        <span class="flex-grow-1">
          {{ t("canvas:analysis.notLinked.message") }}
        </span>
        <DRBtn
          icon="bi-link-45deg"
          :label="t('dashboard:drawings.linkDiagnostic.label')"
          variant="primary"
          size="xs"
          @click="showLinkDialog = true"
        />
      </div>

      <!-- Analyse faite, aucun candidat -->
      <div
        v-if="state.groups.length === 0"
        class="empty-state text-center py-4"
      >
        <PhosphorIcon name="check-circle" :size="32" class="text-grey" />
        <p class="text-grey mt-2 mb-0">
          {{ t("canvas:analysis.noCandidates") }}
        </p>
      </div>

      <!-- Groupes détectés, à valider un par un -->
      <div v-else class="d-flex flex-column gap-2">
        <AnalysisGroupCard
          v-for="group in state.groups"
          :key="group.id"
          :ref="(el) => setCardRef(group.id, el)"
          :group="group"
          :number="state.groupNumber(group.id)"
          :status="state.statusOf(group.id)"
          :is-current="state.currentGroupId === group.id"
          :suggestion="state.aiSuggestions[group.id]"
          :edit="state.edits[group.id]"
          :is-validating="validatingGroupId === group.id"
          :entities="
            state.currentGroupId === group.id ? currentGroupEntities : undefined
          "
          :extra-selected-count="
            state.currentGroupId === group.id ? extraSelectedCount : 0
          "
          :add-mode="state.currentGroupId === group.id && addModeActive"
          @select="locateGroup(group)"
          @validate="handleValidate(group, $event)"
          @ignore="ignoreGroup(group)"
          @restore="restoreGroup(group)"
          @entity-select="focusEntity($event)"
          @entity-remove="handleEntityRemove(group, $event)"
          @entity-move="handleEntityMove(group, $event)"
          @add-selected="handleAddSelected(group)"
          @toggle-add-mode="toggleAddMode"
          @edit-change="handleEditChange(group, $event)"
        />
      </div>
    </template>

    <!-- Pied : nouveau groupe + relance de l'analyse -->
    <template #actions>
      <div class="d-flex flex-column gap-2">
        <DRBtn
          class="w-100"
          icon="bi-plus-lg"
          :label="
            selectionHandles.length
              ? t('canvas:analysis.createGroup.withCount', {
                  count: selectionHandles.length,
                })
              : t('canvas:analysis.createGroup.button')
          "
          variant="primary"
          size="sm"
          :disabled="selectionHandles.length === 0 || state.isAnalyzing"
          @click="handleCreateGroup"
        />
        <!-- L'analyse est un outil en ligne : pas de relance hors connexion -->
        <DRBtn
          class="w-100"
          icon="bi-arrow-clockwise"
          :label="t('canvas:analysis.rerun')"
          variant="outline-white"
          size="sm"
          :loading="state.isAnalyzing"
          :disabled="device.isOnline === false"
          @click="requestAnalysis"
        />
      </div>
    </template>
  </DrawerMenuContent>

  <ConfirmationModal
    v-model="showClearConfirm"
    :title="t('canvas:analysis.clear.title')"
    :message="t('canvas:analysis.clear.message')"
    :confirmLabel="t('canvas:analysis.clear.button')"
    :loading="isClearing"
    danger
    @confirm="handleClearAnalysis"
  />

  <AnalysisLaunchDialog
    v-model="showLaunchDialog"
    :initial-unit="suggestedUnit"
    relaunch
    @confirm="confirmLaunch"
  />

  <DrawingLinkDialog v-model="showLinkDialog" :drawing-id="drawingId" />
</template>

<style lang="scss" scoped>
.not-linked {
  font-size: 0.78rem;
  border: 1px solid var(--canvas-border);
  border-radius: 6px;
  padding: 8px;
  color: var(--canvas-forced);
}

.empty-state {
  font-size: 0.85rem;
}
</style>
